import React, { Fragment, useState } from "react";

import { TableRow, TableCell, makeStyles, Chip, DownloadIcon } from "../helper";
import ChallanSelectInput from "../Inputs/ChallanSelectInput";
import ChallanSearchInput from "../Inputs/ChallanSearchInput";
import DateRangeSelect from "../DateRangeSelect";
import {
  dateMonthYearFormatter,
  defaultTransParamValue,
} from "../../../../data/metadata/transactionMetadata";
import ChallanPopperButton from "../Buttons/ChallanPopperButton";
import { mobileValidator } from "../../../../utils/validators/index";
import Notifications from "../../../../utils/notifications";

const useStyles = () => ({
  selectBox: () => ({ mx: 1, width: 220 }),
  searchBox: () => ({ mx: 1, width: 250 }),
  tableRow: () => ({ "&:last-child td, &:last-child th": { border: 0 } }),
});

const useClasses = (filter) =>
  makeStyles((theme) => ({
    actionBoxWrapper: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    leftSideBoxWrapper: {
      display: "flex",
      width: "20%",
      alignItems: "center",
    },
    middleSideBoxWrapper: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      width: "20%",
      paddingRight: theme.spacing(2),
    },
    rightSideBoxWrapper: {
      display: "flex",
      width: filter ? "60%" : "40%",
      alignItems: "center",
      justifyContent: "flex-end",
    },
    leftSpanner: {
      fontWeight: 700,
      fontSize: 12,
    },
    boxWrapper: {
      display: "flex",
      justifyContent: "flex-end",
    },
    rightSpanner: {
      fontSize: 14,
      fontWeight: 600,
      color: "#E2136E",
      marginLeft: theme.spacing(1),
    },
    crumbBoxContainer: {
      backgroundColor: "#FFEDF6",
      margin: theme.spacing(1, -2, 0),
      padding: theme.spacing(1, 2),
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    crumbBoxWrapper: {
      display: "flex",
      alignItems: "center",

      "& p": {
        margin: 0,
        marginRight: theme.spacing(1),
        color: "#E2136E",
        fontWeight: 600,
      },
    },
  }));

export default function ChallanActionRow({
  span,
  economicCode,
  statusCode,
  searchFields,
  params: globalPrams,
  updateParams,
  downloadCurrent,
}) {
  const [params, setParams] = useState(globalPrams);
  const styles = useStyles();
  const classes = useClasses(!!params.economicCode)();

  function determineState(params) {
    if (params.economicCode != null) return "economicCode";
    else if (params.acsRef != null) return "acsRef";
    else if (params.mobile != null) return "mobile";
    return "bkashTrxId";
  }

  function determineLabel(params) {
    if (params.acsRef != null) return "ACS Reference";
    else if (params.mobile != null) return "Mobile Number";
    return "bKash Transaction ID";
  }

  function determineValue(params) {
    return params[determineState(params)];
  }

  function SearchComponent() {
    const handleChange = (event) => {
      setParams({
        ...params,
        [determineState(params)]: event.target.value,
      });
    };
    return (
      <ChallanSearchInput
        id="search_box"
        autoFocus={true}
        label={`Find By ${determineLabel(params)}`}
        value={determineValue(params)}
        onChange={handleChange}
        sx={styles.searchBox}
      />
    );
  }

  function FilterComponent() {
    const handleChange = (id) => (event) => {
      const updatedParams = {
        ...params,
        [id]: event.target.value,
      };

      if (id === "state" && event.target.value === "all") {
        delete updatedParams.state;
      }

      setParams(updatedParams);
    };

    const handleDateChange = (startDate, endDate) => {
      setParams({
        ...params,
        startTimestamp: startDate.valueOf(),
        endTimestamp: endDate.valueOf(),
      });
    };

    return (
      <Fragment>
        <DateRangeSelect
          endDate={params.endTimestamp}
          startDate={params.startTimestamp}
          onChange={handleDateChange}
        />
        <ChallanSelectInput
          id="filter_economic_code"
          label="Filter By Economic Code"
          onChange={handleChange("economicCode")}
          value={params.economicCode}
          options={economicCode}
          sx={styles.selectBox}
        />
        <ChallanSelectInput
          id="filter_status"
          label="Filter By Status"
          onChange={handleChange("state")}
          value={params.state ? params.state : "all"}
          options={statusCode}
          sx={styles.selectBox}
        />
      </Fragment>
    );
  }

  function determineRender(params) {
    if (params.economicCode) return <FilterComponent />;
    return <SearchComponent />;
  }

  function determineFilter(params) {
    if (params.economicCode)
      return (
        <div className={classes.middleSideBoxWrapper}>
          <div className={classes.boxWrapper}>
            <div className={classes.leftSpanner}>Start Date:</div>
            <div className={classes.rightSpanner}>
              {dateMonthYearFormatter(params.startTimestamp)}
            </div>
          </div>
          <div className={classes.boxWrapper}>
            <div className={classes.leftSpanner}>End Date:</div>{" "}
            <div className={classes.rightSpanner}>
              {dateMonthYearFormatter(params.endTimestamp)}
            </div>
          </div>
        </div>
      );

    return null;
  }

  function validate(params) {
    if (params.economicCode) return true;

    if (params.mobile) {
      const validateMobileFlag = mobileValidator(params.mobile);

      if (!validateMobileFlag) Notifications.error("Phone no invalid.");
      return validateMobileFlag;
    }

    const validationFlag = params.acsRef || params.bkashTrxId || params.mobile;

    if (!validationFlag)
      Notifications.error(`${determineLabel(params)} can not be empty.`);
    return validationFlag;
  }

  function handleFetchClick() {
    if (validate(params)) updateParams(params);
  }

  function handleUpdateParmas(event) {
    const value = event.target.value;
    const paramsLocal = {
      [value]: defaultTransParamValue[value],
    };

    if (value === "economicCode") {
      paramsLocal.endTimestamp = defaultTransParamValue.endTimestamp;
      paramsLocal.startTimestamp = defaultTransParamValue.startTimestamp;
    }

    setParams(paramsLocal);
  }

  const getLabel = (key) => {
    const data = {
      economicCode: "Economic Code",
      mobile: "Mobile Number",
      acsRef: "ACS Reference",
      bkashTrxId: "bKash Transaction ID",
      state: "Status",
      startTimestamp: "Start Date",
      endTimestamp: "End Date",
    };

    return data[key];
  };

  const getValue = (key, value) => {
    const data = {
      economicCode: (value) => value,
      mobile: (value) => value,
      acsRef: (value) => value,
      bkashTrxId: (value) => value,
      state: (value) => statusCode.find((s) => s.value === value).label,
      startTimestamp: (value) => dateMonthYearFormatter(value),
      endTimestamp: (value) => dateMonthYearFormatter(value),
    };

    return data[key](value);
  };

  const decomposeParams = (params) => {
    return Object.entries(params)
      .reverse()
      .map(([key, value]) => {
        const label = getLabel(key);
        const labelValue = getValue(key, value);

        return (
          <Chip
            sx={{
              height: "auto",
              m: 1,
              "& .MuiChip-label": {
                display: "block",
                whiteSpace: "normal",
              },
            }}
            key={key}
            label={`${label}: ${labelValue}`}
            color="primary"
          />
        );
      });
  };

  return (
    <TableRow sx={styles.tableRow}>
      <TableCell colSpan={span}>
        <div className={classes.actionBoxWrapper}>
          <div className={classes.leftSideBoxWrapper}>
            <ChallanSelectInput
              id="search_field"
              label="Choose Search Field"
              onChange={handleUpdateParmas}
              value={determineState(params)}
              options={searchFields}
              sx={styles.selectBox}
            />
          </div>
          {determineFilter(params)}
          <div className={classes.rightSideBoxWrapper}>
            {determineRender(params)}
            <ChallanPopperButton
              variant={"contained"}
              width={100}
              onClick={handleFetchClick}
              gutter={[1, 0, 0, 1]}
              borderColor={"rgba(0, 0, 0, 0"}
              bg={"#E2136E"}
              color={"#FFFFFF"}
            >
              <span>Apply</span>
            </ChallanPopperButton>
          </div>
        </div>
        <div className={classes.crumbBoxContainer}>
          <div className={classes.crumbBoxWrapper}>
            <p>Currently view filtered by: </p>
            <div>{decomposeParams(globalPrams)}</div>
          </div>
          <div>
            <ChallanPopperButton
              width={220}
              onClick={downloadCurrent}
              gutter={[1, 0, 0]}
              borderColor={"#E2136E"}
            >
              <DownloadIcon
                style={{ color: "#E2136E" }}
                sx={{ mr: 1, mt: 0.25 }}
              />
              <span style={{ marginRight: 8 }}>Download as CSV</span>
            </ChallanPopperButton>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}
